import { getQueue } from './api'
import { invoicesOn } from './business-settings'
import { textsReach } from './contact'
import { EMAIL_RULE } from './mailto'
import { getMe } from './me'

// Commercial customers (0080; Liam, 20 Oct 2026, about a business he cleans for: "after the job to tell him its done and
// send an invoice ... maybe if we tag a job as commercial we can generate invoice"; docs/plans/commercial/AGREED.md). One
// switch on the customer, customers.commercial, for all their jobs. With it on, Invoices on, and invoiced each visit
// (not Once a month, which keeps today's flow exactly), Roundly offers the invoice after Done. The rules every screen
// shares are here: the Done pop-up and Owed (src/screens/owed.ts), Today's bar (src/booking.ts), their page's switch
// (src/invoices.ts) and the add box (src/add-details.ts).

export interface CommercialCustomer {
  id: number
  commercial?: number | boolean | null
  invoice_mode?: string | null
}

// A change to their page still waiting on this phone (no signal) is laid over what was read, so the switch, the Done
// pop-up, Today and the add box all show it at once; a refused one is not (AGREED.md 15, Sol C19).
function waiting(id: number, field: 'commercial' | 'invoice_mode' | 'phone' | 'email' | 'invoice_to_email' | 'business_hint_dismissed'): { value: unknown } | null {
  const path = `/api/customers/${id}`
  let out: { value: unknown } | null = null
  for (const q of getQueue()) {
    if (q.method !== 'PATCH' || q.path !== path || q.status === 'conflict' || q.status === 'failed' || !q.body || typeof q.body !== 'object') continue
    const body = q.body as Record<string, unknown>
    if (field in body) out = { value: body[field] }
  }
  return out
}

// Is this customer commercial, with anything waiting on this phone laid over?
export function isCommercial(c: CommercialCustomer): boolean {
  const w = waiting(c.id, 'commercial')
  const v = w ? w.value : c.commercial
  return v === 1 || v === true
}

// Invoiced once a month (their page's "Once a month"), with anything waiting laid over.
function monthly(c: CommercialCustomer): boolean {
  const w = waiting(c.id, 'invoice_mode')
  return (w ? w.value : c.invoice_mode) === 'month'
}
export const isMonthly = monthly

// "Looks like a business" answered Not a business or Keep payment messages (0086, business-hint.ts), waiting laid over.
export function hintDismissed(c: { id: number; business_hint_dismissed?: number | boolean | null }): boolean {
  const w = waiting(c.id, 'business_hint_dismissed')
  const v = w ? w.value : c.business_hint_dismissed
  return v === 1 || v === true
}

// The after-the-job invoice is offered: the owner's phone, Invoices on, a commercial customer invoiced each visit.
export function invoiceAfterEachJob(c: CommercialCustomer | null | undefined): boolean {
  return !!c && getMe()?.role === 'owner' && invoicesOn() && isCommercial(c) && !monthly(c)
}

// Can an invoice reach them: a mobile that takes texts, their own email, or the email their invoices go to (AGREED.md
// 12, Sol C13). Not how they are messaged about visits: a customer set to Don't message can still get an invoice.
export interface InvoiceReachable { id: number; phone?: string | null; email?: string | null; invoice_to_email?: string | null }
export function invoiceReach(c: InvoiceReachable): { text: boolean; email: boolean; any: boolean } {
  const pick = (field: 'phone' | 'email' | 'invoice_to_email'): string => {
    const w = waiting(c.id, field)
    return String((w ? w.value : c[field]) ?? '').trim()
  }
  const text = textsReach(pick('phone'))
  const email = EMAIL_RULE.test(pick('invoice_to_email')) || EMAIL_RULE.test(pick('email'))
  return { text, email, any: text || email }
}
